/**
 * Which species have alternate FORMS, and which have GO event COSTUMES.
 *
 * Both questions are answered from the same generated data
 * (pokemonForms.generated.ts) but they are NOT the same question: a costume is
 * the species wearing something (Ash caps, party hats, GO Fest / WCS sets),
 * while a form is a genuinely different Pokémon entry (Alolan, Vivillon
 * patterns, Unown letters). `FormVariant.kind` carries the split — it cannot be
 * inferred from the slug, see FormKind in lib/types/forms.ts.
 *
 * Built ONCE at module load into two id Sets, because the dex grid filter asks
 * "does this species have forms?" for every one of 1,025 cells on every filter
 * change. Walking 141 form groups per cell would be ~144k comparisons per
 * keystroke; two Set lookups are O(1).
 *
 * A species counts as HAVING FORMS only when it has at least two non-costume
 * entries. Pikachu is the case that makes this matter: it has both real forms
 * (caps, Cosplay) and 54 costumes, and a species whose only extra entries were
 * costumes would otherwise be listed under "Has forms" with nothing to show
 * beyond its base sprite.
 */
import { POKEMON_FORM_GROUPS } from "./generated/pokemonForms.generated";

const speciesWithForms = new Set<number>();
const speciesWithCostumes = new Set<number>();

for (const group of POKEMON_FORM_GROUPS) {
  let forms = 0;
  let costumes = 0;
  for (const f of group.forms) {
    if (f.kind === "costume") costumes += 1;
    else forms += 1;
  }
  if (forms >= 2) speciesWithForms.add(group.speciesId);
  if (costumes > 0) speciesWithCostumes.add(group.speciesId);
}

/** Species ids that have at least two real (non-costume) forms. */
export const SPECIES_WITH_FORMS: ReadonlySet<number> = speciesWithForms;

/** Species ids that have at least one Pokémon GO event costume. */
export const SPECIES_WITH_COSTUMES: ReadonlySet<number> = speciesWithCostumes;

export function hasAltForms(speciesId: number): boolean {
  return speciesWithForms.has(speciesId);
}

export function hasCostumes(speciesId: number): boolean {
  return speciesWithCostumes.has(speciesId);
}
